import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {authService} from '@services/auth.service';
import {isSuperAdmin, isSuperAdminRoleName} from '@utils/roleAccess';
import {
    formatClinicLabel,
    getActiveTenantId,
    getTenantNameFromUser,
    getTenantContextFromJwt,
    normalizeAvailableClinics
} from '@utils/tenant';

const AuthContext = createContext(null);

const resolveTenantName = (tenantId, availableClinics, user) => {
    const fromUser = getTenantNameFromUser(user);

    if (fromUser) {
        return fromUser;
    }

    if (!tenantId) {
        return '';
    }

    const fromList = availableClinics.find(clinic => clinic.id === tenantId);

    if (fromList?.nombre) {
        return fromList.nombre;
    }

    return formatClinicLabel(tenantId, '');
};

const resolveIsSuperAdmin = user => {
    if (authService.isImpersonationViewActive()) {
        return false;
    }

    if (isSuperAdmin(user)) {
        return true;
    }

    const jwtContext = getTenantContextFromJwt(authService.getToken());
    return isSuperAdminRoleName(jwtContext.role_global);
};

const readAuthState = () => {
    const user = authService.getUser();
    const availableClinics = authService.getAvailableClinics();
    const availableEmpresas = authService.getAvailableEmpresas();
    const tenantId = getActiveTenantId();
    const empresaId = authService.getEmpresaId(user);

    return {
        user,
        isAuthenticated: authService.isAuthenticated(),
        tenantId,
        empresaId,
        availableClinics,
        availableEmpresas,
        tenantName: resolveTenantName(tenantId, availableClinics, user),
        isSuperAdmin: resolveIsSuperAdmin(user),
        isAdminEmpresaCliente: authService.isAdminEmpresaCliente(user)
    };
};

export const AuthProvider = ({children}) => {
    const [state, setState] = useState(readAuthState);
    const [isHydrating, setIsHydrating] = useState(() => authService.isAuthenticated());

    const refresh = useCallback(() => {
        setState(readAuthState());
    }, []);

    const hydrateSession = useCallback(async (options = {}) => {
        if (!authService.isAuthenticated()) {
            setIsHydrating(false);
            return null;
        }

        setIsHydrating(true);

        try {
            const result = await authService.hydrateSessionFromToken(options);
            refresh();
            return result;
        } finally {
            setIsHydrating(false);
        }
    }, [refresh]);

    useEffect(() => {
        let mounted = true;

        const bootstrap = async () => {
            if (!authService.isAuthenticated()) {
                if (mounted) {
                    setIsHydrating(false);
                }
                return;
            }

            if (authService.isImpersonationViewActive()) {
                await hydrateSession({skipApi: true});
                return;
            }

            const user = authService.getUser();

            await hydrateSession({
                includeInactive: isSuperAdmin(user)
            });
        };

        bootstrap();

        return () => {
            mounted = false;
        };
    }, [hydrateSession]);

    useEffect(() => {
        const handleChange = () => refresh();

        window.addEventListener('auth-session-changed', handleChange);
        window.addEventListener('auth-user-updated', handleChange);
        window.addEventListener('tenant-context-changed', handleChange);

        return () => {
            window.removeEventListener('auth-session-changed', handleChange);
            window.removeEventListener('auth-user-updated', handleChange);
            window.removeEventListener('tenant-context-changed', handleChange);
        };
    }, [refresh]);

    const logout = useCallback(() => {
        authService.logout();
        setIsHydrating(false);
        refresh();
    }, [refresh]);

    const selectClinic = useCallback(async clinicId => {
        const data = await authService.selectClinic(clinicId);
        refresh();
        return data;
    }, [refresh]);

    const selectEmpresa = useCallback(async empresaId => {
        const data = await authService.selectEmpresa(empresaId);
        refresh();
        return data;
    }, [refresh]);

    const setAvailableClinics = useCallback(clinics => {
        const normalized = authService.setAvailableClinics(normalizeAvailableClinics(clinics));
        refresh();
        return normalized;
    }, [refresh]);

    const setAvailableEmpresas = useCallback(empresas => {
        const normalized = authService.setAvailableEmpresas(empresas);
        refresh();
        return normalized;
    }, [refresh]);

    const value = useMemo(() => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        tenantId: state.tenantId,
        empresaId: state.empresaId,
        tenantName: state.tenantName,
        availableClinics: state.availableClinics,
        availableEmpresas: state.availableEmpresas,
        isSuperAdmin: state.isSuperAdmin,
        isAdminEmpresaCliente: state.isAdminEmpresaCliente,
        isHydrating,
        logout,
        refresh,
        hydrateSession,
        selectClinic,
        selectEmpresa,
        setAvailableClinics,
        setAvailableEmpresas
    }), [state, isHydrating, logout, refresh, hydrateSession, selectClinic, selectEmpresa, setAvailableClinics, setAvailableEmpresas]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }

    return context;
};
